import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { t } from 'i18next';
import React, { forwardRef, useCallback } from 'react';
import { SharedValue } from 'react-native-reanimated';
import { useDispatch } from 'react-redux';

import { tournamentsActiveRequest } from '../../../store/tournaments/tournamentsActions';
import { formatBigNumbers } from '../../../utils/helpers/numberFormat';
import { RootStackParamList } from '../../navigator';
import { STAGE_WHEEL_POSE } from '../common/carouselStage';
import { WheelCarousel, WheelCarouselHandle } from '../common/WheelCarousel';
import TournamentCarouselCard from '../Tournaments/TournamentCarouselCard';
import { useTournamentRegistration } from '../Tournaments/useTournamentRegistration';
import { SnG } from './TournamentsSection';

export type TournamentsCarouselHandle = WheelCarouselHandle;

type Nav = NativeStackNavigationProp<RootStackParamList>;

// The design shows a whole-chip entry ("33,000"); FormatWithThousandSeparator appends decimals.
const groupThousands = (value: number) =>
	String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const DEFAULT_SEATS = 8;

export type TournamentsCarouselItem = {
	id: string;
	sng: SnG;
};

type Props = {
	items: TournamentsCarouselItem[];
	activeIndex: number;
	onActiveIndexChange: (index: number) => void;
	width: number;
	height: number;
	cardWidth: number;
	cardHeight: number;
	progress?: SharedValue<number>;
	/** A card tap - the section opens the tournament's info modal. */
	onOpenTournament?: (sng: SnG) => void;
};

/**
 * One card, with its own registration state. The register/active-tables actions need hooks, so
 * each slide gets its own component rather than being built inside the carousel's renderItem.
 */
export const TournamentSlide = ({
	sng,
	cardWidth,
	onOpen,
	shape,
}: {
	sng: SnG;
	cardWidth: number;
	onOpen?: (sng: SnG) => void;
	shape?: 'tall' | 'stage';
}) => {
	const { navigate } = useNavigation<Nav>();
	const dispatch = useDispatch();

	const data = sng as any;
	const roomId = data.room_id ?? data.roomId ?? data.room ?? data.table ?? data.tableId;
	const tournamentId = String(sng?.id ?? '');

	const { isSigned, handleRegisterPress } = useTournamentRegistration({
		tournamentId,
		roomId: roomId != null ? String(roomId) : undefined,
		sng,
	});

	const handleActiveTables = useCallback(() => {
		if (roomId == null) return;
		dispatch(tournamentsActiveRequest({ roomId: String(roomId) }));
		const roomName = sng?.entry_icon
			? sng.entry_icon.charAt(0).toUpperCase() + sng.entry_icon.slice(1)
			: '';
		navigate('ActiveTournament', { roomId: String(roomId), roomName });
	}, [dispatch, navigate, roomId, sng?.entry_icon]);

	const seated = Array.isArray(sng?.users) ? sng.users.length : 0;
	const seats = Number(sng?.players) || DEFAULT_SEATS;

	return (
		<TournamentCarouselCard
			tier={sng.entry_icon}
			width={cardWidth}
			shape={shape}
			prizeLabel={t('T_SITE_TOURNAMENT_WIN')}
			prizeValue={formatBigNumbers(Number(sng.winAmount) || 0)}
			entryLabel={t('T_SITE_TOURNAMENT_PLAYERS_ENTRY')}
			entryValue={groupThousands(Number(sng.entry_size) || 0)}
			playersLabel={t('T_SITE_TOURNAMENT_PLAYERS', { defaultValue: 'Players' })}
			playersValue={t('T_SITE_TOURNAMENT_PLAYERS_OF', {
				defaultValue: '{{seated}} of {{seats}}',
				seated,
				seats,
			})}
			registerLabel={isSigned ? t('T_SITE_TOURNAMENT_UNREGISTER') : t('T_SITE_TOURNAMENT_REGISTER')}
			activeTablesLabel={t('T_SITE_TOURNAMENT_ACTIVE_ROOMS')}
			onRegister={handleRegisterPress}
			onActiveTables={handleActiveTables}
			onPress={onOpen ? () => onOpen(sng) : undefined}
		/>
	);
};

const TournamentsCarouselInner = (
	{
		items,
		activeIndex,
		onActiveIndexChange,
		width,
		height,
		cardWidth,
		cardHeight,
		progress,
		onOpenTournament,
	}: Props,
	ref: React.Ref<TournamentsCarouselHandle>,
) => {
	const renderItem = useCallback(
		({ item }: { item: TournamentsCarouselItem; index: number }) => (
			<TournamentSlide
				sng={item.sng}
				cardWidth={cardWidth}
				onOpen={onOpenTournament}
				shape='stage'
			/>
		),
		[cardWidth, onOpenTournament],
	);

	return (
		<WheelCarousel
			ref={ref}
			data={items}
			renderItem={renderItem}
			activeIndex={activeIndex}
			onActiveIndexChange={onActiveIndexChange}
			width={width}
			height={height}
			cardWidth={cardWidth}
			cardHeight={cardHeight}
			progress={progress}
			pose={STAGE_WHEEL_POSE}
			itemWidthScale={1}
		/>
	);
};

export const TournamentsCarousel = forwardRef(TournamentsCarouselInner);

export default TournamentsCarousel;
